import { createContext, use, useState, type ReactNode } from 'react'

const storageKey = 'momentos-cart:v1'

type CartState = { offerIds: readonly string[] }
type CartActions = {
  add: (offerId: string) => void
  remove: (offerId: string) => void
  clear: () => void
}
type CartContextValue = { state: CartState; actions: CartActions }

const CartContext = createContext<CartContextValue | null>(null)

function loadInitialCart(): CartState {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return { offerIds: [] }
    const value = JSON.parse(raw) as { version?: unknown; offerIds?: unknown }
    if (value.version !== 1 || !Array.isArray(value.offerIds)) return { offerIds: [] }
    return {
      offerIds: [...new Set(value.offerIds.filter((id): id is string => typeof id === 'string'))],
    }
  } catch {
    return { offerIds: [] }
  }
}

function persist(state: CartState): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ version: 1, offerIds: state.offerIds }))
  } catch {
    // O carrinho continua em memória quando o navegador bloqueia o armazenamento.
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CartState>(loadInitialCart)

  function update(updater: (current: CartState) => CartState): void {
    setState((current) => {
      const next = updater(current)
      persist(next)
      return next
    })
  }

  const actions: CartActions = {
    add: (offerId) =>
      update((current) =>
        current.offerIds.includes(offerId) ? current : { offerIds: [...current.offerIds, offerId] },
      ),
    remove: (offerId) =>
      update((current) => ({ offerIds: current.offerIds.filter((id) => id !== offerId) })),
    clear: () => update(() => ({ offerIds: [] })),
  }

  return <CartContext value={{ state, actions }}>{children}</CartContext>
}

export function useCart(): CartContextValue {
  const value = use(CartContext)
  if (!value) throw new Error('useCart precisa estar dentro de CartProvider')
  return value
}
